import React, { useState, useEffect } from 'react';
import { api } from './api.js';
import styles from './AddAffiliateForm.module.css';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';

const AddAffiliateForm = ({ onSave, onClose, initialData, isEditing }) => {
  const [nuevoAfiliado, setNuevoAfiliado] = useState({
    codigoAfiliado: '',
    sector: '',
    nombresTitular: '',
    apellidosTitular: '',
    cedulaTitular: '',
    tipoDocumento: 'Cédula',
    fechaNacimiento: '',
    telefonoTitular: '',
    correoElectronico: '',
    direccionTitular: '',
    ciudad: '',
    provincia: '',
    totalPagarMensual: '',
    estado: 'activo',
    tipoSangre: '',
    enfermedadesCronicas: '',
    discapacidad: '',
    alergias: '',
    medicamentos: '',
    edad: '',
    sexo: '',
    estadoCivil: '',
    nacionalidad: '',
    pasaporte: '',
    referenciaUbicacion: '',
    observacionesEvaluador: '',
    exonerado: false,
    dependencias: [],
    fechaCreacion: '',
    fechaInicioAfiliacion: '',
  });
  

  const [nuevaDependencia, setNuevaDependencia] = useState({
    nombre: '',
    apellido: '',
    cedula: '',
    parentesco: '',
  });

  const [sectores, setSectores] = useState([]);
  const [errorDependencia, setErrorDependencia] = useState('');
  const [dependenciaEditando, setDependenciaEditando] = useState(null);
  const [loading, setLoading] = useState(false);
  const [camposConError, setCamposConError] = useState([]); // Campos con error de validación

  // Cargar datos iniciales si estamos en modo edición
  useEffect(() => {
    if (isEditing && initialData) {
      setNuevoAfiliado({
        codigoAfiliado: initialData.codigoAfiliado ?? '',
        sector: initialData.sector ?? '',
        nombresTitular: initialData.nombresTitular ?? '',
        apellidosTitular: initialData.apellidosTitular ?? '',
        cedulaTitular: initialData.cedulaTitular ?? '',
        tipoDocumento: initialData.tipoDocumento ?? 'Cédula',
        fechaNacimiento: initialData.fechaNacimiento ?? '',
        telefonoTitular: initialData.telefonoTitular ?? '',
        correoElectronico: initialData.correoElectronico ?? '',
        direccionTitular: initialData.direccionTitular ?? '',
        ciudad: initialData.ciudad ?? '',
        provincia: initialData.provincia ?? '',
        totalPagarMensual: initialData.totalPagarMensual ?? '',
        estado: initialData.estado ?? 'activo',
        tipoSangre: initialData.tipoSangre ?? '',
        enfermedadesCronicas: initialData.enfermedadesCronicas ?? '',
        discapacidad: initialData.discapacidad ?? '',
        alergias: initialData.alergias ?? '',
        medicamentos: initialData.medicamentos ?? '',
        edad: initialData.edad ?? '',
        sexo: initialData.sexo ?? '',
        estadoCivil: initialData.estadoCivil ?? '',
        nacionalidad: initialData.nacionalidad ?? '',
        pasaporte: initialData.pasaporte ?? '',
        referenciaUbicacion: initialData.referenciaUbicacion ?? '',
        observacionesEvaluador: initialData.observacionesEvaluador ?? '',
        exonerado: initialData.exonerado ?? false,
        dependencias: initialData.dependencias ?? [],
        fechaCreacion: initialData.fechaCreacion ?? '',
        fechaInicioAfiliacion: initialData.fechaInicioAfiliacion ?? '',
      });
    }
  }, [isEditing, initialData]);

  // Cargar sectores
  useEffect(() => {
    const fetchSectores = async () => {
      try {
        const response = await api.getSectors();
        if (response && Array.isArray(response)) {
          setSectores(response);
        }
      } catch (error) {
        console.error('Error al cargar sectores:', error);
        alert('Hubo un error al cargar los sectores.');
      }
    };
    fetchSectores();
  }, []);

  // Generar nuevo código solo si no estamos editando
  useEffect(() => {
    const fetchUltimoCodigo = async () => {
      if (isEditing) return; // No generar nuevo código si estamos editando

      try {
        const afiliados = await api.getAfiliados();
        if (afiliados && afiliados.length > 0) {
          const codigos = afiliados.map(a => {
            const match = a.codigoAfiliado?.match(/AM-JFP(\d+)/);
            return match ? parseInt(match[1]) : 0;
          });
          const ultimoCodigo = Math.max(...codigos, 0);
          const nuevoCodigo = `AM-JFP${(ultimoCodigo + 1).toString().padStart(4, '0')}`;
          
          setNuevoAfiliado(prev => ({
            ...prev,
            codigoAfiliado: nuevoCodigo,
          }));
        } else {
          setNuevoAfiliado(prev => ({
            ...prev,
            codigoAfiliado: 'AM-JFP0001',
          }));
        }
      } catch (error) {
        console.error('Error al obtener el último código:', error);
        setNuevoAfiliado(prev => ({
          ...prev,
          codigoAfiliado: 'AM-JFP0001',
        }));
      }
    };

    fetchUltimoCodigo();
  }, [isEditing]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNuevoAfiliado((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleDependenciaChange = (e) => {
    const { name, value } = e.target;
    setNuevaDependencia((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const agregarDependencia = () => {
    if (!nuevaDependencia.nombre || !nuevaDependencia.apellido || 
        !nuevaDependencia.cedula || !nuevaDependencia.parentesco) {
      setErrorDependencia('Todos los campos de la dependencia son obligatorios');
      return;
    }
    setErrorDependencia('');

    if (dependenciaEditando !== null) {
      setNuevoAfiliado((prevState) => {
        const dependenciasActualizadas = [...prevState.dependencias];
        dependenciasActualizadas[dependenciaEditando] = nuevaDependencia;
        return {
          ...prevState,
          dependencias: dependenciasActualizadas,
        };
      });
      setDependenciaEditando(null);
    } else {
      setNuevoAfiliado((prevState) => ({
        ...prevState,
        dependencias: [...prevState.dependencias, nuevaDependencia],
      }));
    }

    setNuevaDependencia({ nombre: '', apellido: '', cedula: '', parentesco: '' });
  };

  const eliminarDependencia = (index) => {
    setNuevoAfiliado(prevState => ({
      ...prevState,
      dependencias: prevState.dependencias.filter((_, i) => i !== index),
    }));
  };

  const editarDependencia = (index) => {
    setNuevaDependencia(nuevoAfiliado.dependencias[index]);
    setDependenciaEditando(index);
  };

  // Helper para verificar si un campo tiene error
  const tieneError = (nombreCampo) => camposConError.includes(nombreCampo);
  
  // Estilo para campos con error
  const estiloError = {
    borderColor: '#ff0000',
    backgroundColor: '#ffeeee'
  };

  const handleSave = async () => {
    if (loading) return;
    setLoading(true);
    setCamposConError([]); // Limpiar errores previos
  
    try {
      // Validar campos obligatorios
      const errores = [];
      
      if (!nuevoAfiliado.nombresTitular) errores.push('nombresTitular');
      if (!nuevoAfiliado.apellidosTitular) errores.push('apellidosTitular');
      if (!nuevoAfiliado.cedulaTitular) errores.push('cedulaTitular');
      if (!nuevoAfiliado.sector) errores.push('sector');
      if (!nuevoAfiliado.telefonoTitular) errores.push('telefonoTitular');
      if (!nuevoAfiliado.direccionTitular) errores.push('direccionTitular');
      if (!nuevoAfiliado.totalPagarMensual) errores.push('totalPagarMensual');
      if (!nuevoAfiliado.tipoDocumento) errores.push('tipoDocumento');
      if (!nuevoAfiliado.edad) errores.push('edad');
      if (!nuevoAfiliado.sexo) errores.push('sexo');
      if (!nuevoAfiliado.fechaNacimiento) errores.push('fechaNacimiento');
      if (!nuevoAfiliado.ciudad) errores.push('ciudad');
      if (!nuevoAfiliado.provincia) errores.push('provincia');
      if (!nuevoAfiliado.fechaInicioAfiliacion) errores.push('fechaInicioAfiliacion');

      if (errores.length > 0) {
        setCamposConError(errores);
        alert("Por favor completa TODOS los campos obligatorios (marcados con *)");
        setLoading(false);
        return;
      }

      const dataToSave = {
        codigoAfiliado: nuevoAfiliado.codigoAfiliado,
        sector: nuevoAfiliado.sector,
        nombresTitular: nuevoAfiliado.nombresTitular,
        apellidosTitular: nuevoAfiliado.apellidosTitular,
        cedulaTitular: nuevoAfiliado.cedulaTitular,
        tipoDocumento: nuevoAfiliado.tipoDocumento ?? 'Cédula',
        fechaNacimiento: nuevoAfiliado.fechaNacimiento ?? '',
        telefonoTitular: nuevoAfiliado.telefonoTitular,
        correoElectronico: nuevoAfiliado.correoElectronico,
        direccionTitular: nuevoAfiliado.direccionTitular,
        ciudad: nuevoAfiliado.ciudad,
        provincia: nuevoAfiliado.provincia,
        totalPagarMensual: parseFloat(nuevoAfiliado.totalPagarMensual) || 0,
        tipoSangre: nuevoAfiliado.tipoSangre ?? '',
        enfermedadesCronicas: nuevoAfiliado.enfermedadesCronicas ?? '',
        discapacidad: nuevoAfiliado.discapacidad ?? '',
        alergias: nuevoAfiliado.alergias ?? '',
        medicamentos: nuevoAfiliado.medicamentos ?? '',
        edad: nuevoAfiliado.edad ? parseInt(nuevoAfiliado.edad) : '',
        sexo: nuevoAfiliado.sexo ?? '',
        estadoCivil: nuevoAfiliado.estadoCivil ?? '',
        nacionalidad: nuevoAfiliado.nacionalidad ?? '',
        pasaporte: nuevoAfiliado.pasaporte ?? '',
        referenciaUbicacion: nuevoAfiliado.referenciaUbicacion ?? '',
        observacionesEvaluador: nuevoAfiliado.observacionesEvaluador ?? '',
        exonerado: nuevoAfiliado.exonerado ? 1 : 0,
        fechaInicioAfiliacion: nuevoAfiliado.fechaInicioAfiliacion ?? new Date().toISOString().split('T')[0],
        estado: 'activo'
      };
      
      // LOG para verificar qué se está enviando
      console.log('═══════════════════════════════════════');
      console.log('📤 DATOS ENVIADOS AL SERVIDOR:');
      console.log('ID del afiliado:', initialData?.idAfiliado);
      console.table(dataToSave);
      console.log('═══════════════════════════════════════');

      let afiliadoId;
      if (isEditing && initialData?.idAfiliado) {
        // Actualizar afiliado existente
        console.warn('🔴 CRÍTICO: idAfiliado = ' + initialData.idAfiliado + ' (tipo: ' + typeof initialData.idAfiliado + ')');
        const updateResponse = await api.updateAfiliado(initialData.idAfiliado, dataToSave);
        console.log('✅ Respuesta UPDATE:', updateResponse);
        afiliadoId = initialData.idAfiliado;
      } else {
        // Crear nuevo afiliado
        const response = await api.createAfiliado(dataToSave);
        console.log('✅ Respuesta CREATE:', response);
        afiliadoId = response?.idAfiliado;
        if (!afiliadoId) {
          console.error('❌ No se recibió idAfiliado en la respuesta:', response);
        }
      }

      // Guardar dependencias ANTES de mostrar éxito
      let dependenciasError = null;
      if (nuevoAfiliado.dependencias && nuevoAfiliado.dependencias.length > 0) {
        try {
          for (const dep of nuevoAfiliado.dependencias) {
            const depData = {
              idAfiliado: afiliadoId,
              nombre: dep.nombre,
              apellido: dep.apellido,
              cedula: dep.cedula,
              parentesco: dep.parentesco
            };
            
            if (dep.idDependencia) {
              await api.updateDependencia(dep.idDependencia, depData);
            } else {
              await api.createDependencia(depData);
            }
          }
        } catch (depError) {
          console.warn('⚠️ Advertencia al guardar dependencias:', depError);
          dependenciasError = depError;
          // No interrumpir - el afiliado ya se guardó
        }
      }
      
      // Mostrar éxito DESPUÉS de guardar todo
      if (dependenciasError) {
        alert(isEditing ? "Afiliado actualizado exitosamente.\n(Nota: Hubo un problema al guardar algunas dependencias)" : "Afiliado guardado exitosamente.\n(Nota: Hubo un problema al guardar algunas dependencias)");
      } else {
        alert(isEditing ? "Afiliado actualizado exitosamente." : "Afiliado guardado exitosamente.");
      }
  
      // Limpiar el formulario después de guardar
      setNuevoAfiliado({
        codigoAfiliado: '',
        sector: '',
        nombresTitular: '',
        apellidosTitular: '',
        cedulaTitular: '',
        tipoDocumento: 'Cédula',
        fechaNacimiento: '',
        telefonoTitular: '',
        correoElectronico: '',
        direccionTitular: '',
        ciudad: '',
        provincia: '',
        totalPagarMensual: '',
        tipoSangre: '',
        enfermedadesCronicas: '',
        discapacidad: '',
        alergias: '',
        medicamentos: '',
        edad: '',
        sexo: '',
        estadoCivil: '',
        nacionalidad: '',
        pasaporte: '',
        referenciaUbicacion: '',
        observacionesEvaluador: '',
        exonerado: false,
        dependencias: [],
        fechaCreacion: '',
        fechaInicioAfiliacion: '',
      });
  
      setNuevaDependencia({ nombre: '', apellido: '', cedula: '', parentesco: '' });
      onClose();
    } catch (error) {
      console.error("❌ ERROR al guardar o actualizar el afiliado:");
      console.error(error);
      console.error("Stack:", error.stack);
      alert("Hubo un error al guardar el afiliado: " + (error.message || String(error)));
    } finally {
      setLoading(false);
    }
  };
  
  
  return (
    <div className={styles.addAffiliateForm}>
      <h3>{isEditing ? 'Editar Afiliado' : 'Agregar Nuevo Afiliado'}</h3>
      <form>
        {/* SECCIÓN 1: INFORMACIÓN BÁSICA */}
        <div className={styles.formSection}>
          <h4>Información Básica</h4>
          <label>
            ID Afiliado: {nuevoAfiliado.codigoAfiliado}
          </label>
          <label>
            Sector: <span style={{ color: 'red', fontSize: '12px' }}>*</span>
            <select 
              name="sector" 
              value={nuevoAfiliado.sector} 
              onChange={handleInputChange}
              style={tieneError('sector') ? estiloError : {}}
              required
            >
              <option value="">Selecciona un sector</option>
              {sectores.map((sector) => (
                <option key={sector.id} value={sector.nombre || sector.nombre_sector}>
                  {sector.nombre || sector.nombre_sector}
                </option>
              ))}
            </select>
          </label>
          <label>
            Fecha de Inicio de Afiliación: <span style={{ color: 'red', fontSize: '12px' }}>*</span>
            <DatePicker
              selected={nuevoAfiliado.fechaInicioAfiliacion ? new Date(nuevoAfiliado.fechaInicioAfiliacion) : null}
              onChange={(date) => {
                if (date) {
                  setNuevoAfiliado((prevState) => ({
                    ...prevState,
                    fechaInicioAfiliacion: date.toISOString().split("T")[0],
                  }));
                }
              }}
              dateFormat="yyyy-MM-dd"
              placeholderText="Selecciona una fecha"
              className={styles.datePicker}
              style={tieneError('fechaInicioAfiliacion') ? estiloError : {}}
              shouldCloseOnSelect={true}
              autoComplete="off"
              required
            />
          </label>
        </div>

        {/* SECCIÓN 2: INFORMACIÓN PERSONAL */}
        <div className={styles.formSection}>
          <h4>Información Personal</h4>
          <label>
            Nombres del Titular: <span style={{ color: 'red', fontSize: '12px' }}>*</span>
            <input 
              type="text" 
              name="nombresTitular" 
              value={nuevoAfiliado.nombresTitular} 
              onChange={handleInputChange}
              style={tieneError('nombresTitular') ? estiloError : {}}
              required 
            />
          </label>

          <label>
            Apellidos del Titular: <span style={{ color: 'red', fontSize: '12px' }}>*</span>
            <input 
              type="text" 
              name="apellidosTitular" 
              value={nuevoAfiliado.apellidosTitular} 
              onChange={handleInputChange}
              style={tieneError('apellidosTitular') ? estiloError : {}}
              required 
            />
          </label>

          <label>
            Tipo de Documento: <span style={{ color: 'red', fontSize: '12px' }}>*</span>
            <select 
              name="tipoDocumento" 
              value={nuevoAfiliado.tipoDocumento} 
              onChange={handleInputChange}
              style={tieneError('tipoDocumento') ? estiloError : {}}
            >
              <option value="">Selecciona tipo</option>
              <option value="Cédula">Cédula</option>
              <option value="Pasaporte">Pasaporte</option>
              <option value="Licencia">Licencia</option>
              <option value="Otro">Otro</option>
            </select>
          </label>

          {nuevoAfiliado.tipoDocumento === 'Cédula' && (
            <label>
              Cédula del Titular: <span style={{ color: 'red', fontSize: '12px' }}>*</span>
              <input 
                type="text" 
                name="cedulaTitular" 
                value={nuevoAfiliado.cedulaTitular} 
                onChange={handleInputChange}
                style={tieneError('cedulaTitular') ? estiloError : {}}
                required
              />
            </label>
          )}

          {nuevoAfiliado.tipoDocumento === 'Pasaporte' && (
            <label>
              Pasaporte: <span style={{ color: 'red', fontSize: '12px' }}>*</span>
              <input 
                type="text" 
                name="pasaporte" 
                value={nuevoAfiliado.pasaporte} 
                onChange={handleInputChange}
                style={tieneError('cedulaTitular') ? estiloError : {}}
                required
              />
            </label>
          )}

          {nuevoAfiliado.tipoDocumento === 'Licencia' && (
            <label>
              Número de Licencia: <span style={{ color: 'red', fontSize: '12px' }}>*</span>
              <input 
                type="text" 
                name="cedulaTitular" 
                value={nuevoAfiliado.cedulaTitular} 
                onChange={handleInputChange}
                style={tieneError('cedulaTitular') ? estiloError : {}}
                required
                placeholder="Ingresa el número de licencia"
              />
            </label>
          )}

          {nuevoAfiliado.tipoDocumento === 'Otro' && (
            <label>
              Número del Documento: <span style={{ color: 'red', fontSize: '12px' }}>*</span>
              <input 
                type="text" 
                name="cedulaTitular" 
                value={nuevoAfiliado.cedulaTitular} 
                onChange={handleInputChange}
                style={tieneError('cedulaTitular') ? estiloError : {}}
                required
                placeholder="Ingresa el número del documento"
              />
            </label>
          )}

          <label>
            Fecha de Nacimiento: <span style={{ color: 'red', fontSize: '12px' }}>*</span>
            <DatePicker
              selected={nuevoAfiliado.fechaNacimiento ? new Date(nuevoAfiliado.fechaNacimiento) : null}
              onChange={(date) => {
                if (date) {
                  setNuevoAfiliado((prevState) => ({
                    ...prevState,
                    fechaNacimiento: date.toISOString().split("T")[0],
                  }));
                }
              }}
              dateFormat="yyyy-MM-dd"
              placeholderText="Selecciona tu fecha de nacimiento"
              className={styles.datePicker}
              style={tieneError('fechaNacimiento') ? estiloError : {}}
            />
          </label>

          <label>
            Edad: <span style={{ color: 'red', fontSize: '12px' }}>*</span>
            <input 
              type="number" 
              name="edad" 
              value={nuevoAfiliado.edad} 
              onChange={handleInputChange}
              style={tieneError('edad') ? estiloError : {}}
            />
          </label>

          <label>
            Sexo: <span style={{ color: 'red', fontSize: '12px' }}>*</span>
            <select 
              name="sexo" 
              value={nuevoAfiliado.sexo} 
              onChange={handleInputChange}
              style={tieneError('sexo') ? estiloError : {}}
            >
              <option value="">Selecciona sexo</option>
              <option value="M">Masculino</option>
              <option value="F">Femenino</option>
              <option value="O">Otro</option>
            </select>
          </label>

          <label>
            Estado Civil:
            <select 
              name="estadoCivil" 
              value={nuevoAfiliado.estadoCivil} 
              onChange={handleInputChange}
            >
              <option value="">Selecciona estado civil</option>
              <option value="Soltero">Soltero</option>
              <option value="Casado">Casado</option>
              <option value="Divorciado">Divorciado</option>
              <option value="Viudo">Viudo</option>
              <option value="Unión Libre">Unión Libre</option>
            </select>
          </label>

          <label>
            Nacionalidad:
            <input 
              type="text" 
              name="nacionalidad" 
              value={nuevoAfiliado.nacionalidad} 
              onChange={handleInputChange}
            />
          </label>
        </div>

        {/* SECCIÓN 3: INFORMACIÓN DE CONTACTO */}
        <div className={styles.formSection}>
          <h4>Información de Contacto</h4>
          <label>
            Teléfono del Titular: <span style={{ color: 'red', fontSize: '12px' }}>*</span>
            <input 
              type="text" 
              name="telefonoTitular" 
              value={nuevoAfiliado.telefonoTitular} 
              onChange={handleInputChange}
              style={tieneError('telefonoTitular') ? estiloError : {}}
              required 
            />
          </label>

          <label>
            Correo Electrónico:
            <input 
              type="email" 
              name="correoElectronico" 
              value={nuevoAfiliado.correoElectronico} 
              onChange={handleInputChange}
            />
          </label>

          <label>
            Dirección Completa: <span style={{ color: 'red', fontSize: '12px' }}>*</span>
            <input 
              type="text" 
              name="direccionTitular" 
              value={nuevoAfiliado.direccionTitular} 
              onChange={handleInputChange}
              style={tieneError('direccionTitular') ? estiloError : {}}
              required 
            />
          </label>

          <label>
            Ciudad: <span style={{ color: 'red', fontSize: '12px' }}>*</span>
            <input 
              type="text" 
              name="ciudad" 
              value={nuevoAfiliado.ciudad} 
              onChange={handleInputChange}
              style={tieneError('ciudad') ? estiloError : {}}
            />
          </label>

          <label>
            Provincia: <span style={{ color: 'red', fontSize: '12px' }}>*</span>
            <input 
              type="text" 
              name="provincia" 
              value={nuevoAfiliado.provincia} 
              onChange={handleInputChange}
              style={tieneError('provincia') ? estiloError : {}}
            />
          </label>

          <label>
            Referencia de Ubicación:
            <textarea
              name="referenciaUbicacion" 
              value={nuevoAfiliado.referenciaUbicacion} 
              onChange={handleInputChange}
              placeholder="Ej: Cerca del parque, en la esquina de la iglesia..."
            />
          </label>
        </div>

        {/* SECCIÓN 4: INFORMACIÓN MÉDICA */}
        <div className={styles.formSection}>
          <h4>Información Médica</h4>

          <label>
            Tipo de Sangre:
            <select 
              name="tipoSangre" 
              value={nuevoAfiliado.tipoSangre} 
              onChange={handleInputChange}
            >
              <option value="">Selecciona tipo de sangre</option>
              <option value="O+">O+</option>
              <option value="O-">O-</option>
              <option value="A+">A+</option>
              <option value="A-">A-</option>
              <option value="B+">B+</option>
              <option value="B-">B-</option>
              <option value="AB+">AB+</option>
              <option value="AB-">AB-</option>
            </select>
          </label>

          <label>
            Enfermedades Crónicas:
            <textarea 
              name="enfermedadesCronicas" 
              value={nuevoAfiliado.enfermedadesCronicas} 
              onChange={handleInputChange}
              placeholder="Ej: Diabetes, Hipertensión..."
            />
          </label>

          <label>
            Discapacidad:
            <textarea 
              name="discapacidad" 
              value={nuevoAfiliado.discapacidad} 
              onChange={handleInputChange}
              placeholder="Si tiene alguna discapacidad, descríbela aquí"
            />
          </label>

          <label>
            Alergias:
            <textarea 
              name="alergias" 
              value={nuevoAfiliado.alergias} 
              onChange={handleInputChange}
              placeholder="Ej: Penicilina, Maní, Gluten..."
            />
          </label>

          <label>
            Medicamentos que Usa Regularmente:
            <textarea 
              name="medicamentos" 
              value={nuevoAfiliado.medicamentos} 
              onChange={handleInputChange}
              placeholder="Ej: Metformina, Losartán..."
            />
          </label>
        </div>

        {/* SECCIÓN 5: INFORMACIÓN ECONÓMICA */}
        <div className={styles.formSection}>
          <h4>Información Económica</h4>

          <label>
            Total a Pagar Mensual: <span style={{ color: 'red', fontSize: '12px' }}>*</span>
            <input 
              type="number" 
              name="totalPagarMensual" 
              value={nuevoAfiliado.totalPagarMensual} 
              onChange={handleInputChange}
              style={tieneError('totalPagarMensual') ? estiloError : {}}
              required 
            />
          </label>

          <label>
            Exonerado:
            <input 
              type="checkbox" 
              name="exonerado" 
              checked={nuevoAfiliado.exonerado} 
              onChange={(e) => setNuevoAfiliado(prev => ({ ...prev, exonerado: e.target.checked }))}
              style={{ width: 'auto', marginLeft: '5px' }}
            />
          </label>

          <label>
            Observaciones del Evaluador:
            <textarea
              name="observacionesEvaluador" 
              value={nuevoAfiliado.observacionesEvaluador} 
              onChange={handleInputChange}
              placeholder="Agrrega cualquier observación relevante..."
            />
          </label>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
          <h4 style={{ margin: 0 }}>Agregar Dependencia</h4>
          <button 
            type="button" 
            className={styles.iconButton} 
            onClick={agregarDependencia}
            title={dependenciaEditando !== null ? 'Guardar Cambios' : 'Agregar Dependencia'}
          >
            <FontAwesomeIcon icon={faPlus} />
          </button>
        </div>
        <div className={styles.dependenciasSection}>
          <label>
            Nombre:
            <input type="text" name="nombre" value={nuevaDependencia.nombre} onChange={handleDependenciaChange} />
          </label>
          <label>
            Apellido:
            <input type="text" name="apellido" value={nuevaDependencia.apellido} onChange={handleDependenciaChange} />
          </label>
          <label>
            Cédula:
            <input type="text" name="cedula" value={nuevaDependencia.cedula} onChange={handleDependenciaChange} />
          </label>
          <label>
            Parentesco:
            <input type="text" name="parentesco" value={nuevaDependencia.parentesco} onChange={handleDependenciaChange} />
          </label>
        </div>

        {errorDependencia && <p className={styles.error}>{errorDependencia}</p>}

        <div className={styles.formSection}>
          <h4>Dependencias Agregadas</h4>
          {nuevoAfiliado.dependencias.length > 0 && (
            <table className={styles.dependenciasTable}>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Apellido</th>
                  <th>Cédula</th>
                  <th>Parentesco</th>
                  <th>Acción</th>
                </tr>
              </thead>
              <tbody>
                {nuevoAfiliado.dependencias.map((dependencia, index) => (
                  <tr key={index}>
                    <td>{dependencia.nombre}</td>
                    <td>{dependencia.apellido}</td>
                    <td>{dependencia.cedula}</td>
                    <td>{dependencia.parentesco}</td>
                    <td>
                      <button 
                        type="button" 
                        onClick={() => editarDependencia(index)}
                        className={styles.editButton}
                        style={{ marginRight: '10px' }} // Agregar margen derecho
                      >
                        Editar
                      </button>
                      <button 
                        type="button" 
                        onClick={() => eliminarDependencia(index)}
                        className={styles.deleteButton}
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className={styles.formActions}>
          <button 
            type="button" 
            onClick={handleSave}
            disabled={loading}
          >
            {isEditing ? 'Guardar Cambios' : 'Guardar'}
          </button>
          <button type="button" onClick={onClose}>Cancelar</button>
        </div>
      </form>
    </div>
  );
};

export default AddAffiliateForm;